import React, { useCallback, useEffect, useRef, useState } from 'react';
import UsersTable from '../../components/tables/UsersTable';
import UserForm from '../../components/Users/UserForm';
import useUsersService from '../../services/useUsersService';
import useAuthService from '../../services/authService';
import { getSessionUser } from '../../services/authSession';
import { User } from '../../models/UserModel';
import showToast from '../../utils/toast';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../components/Base/ConfirmAlert';
import Pagination from '../../components/Base/Pagination';
import TableTextSearch from '../../components/Base/TableTextSearch';
import { isAdministrator } from '../../hooks/usePermissions';
import { getPasswordRulesErrorMessage } from '../../utils/passwordRules';

const UsersPage: React.FC = () => {
  const svc = useUsersService();
  const { list, store, update, remove, create, edit, fetching } = svc;
  const { adminResetPassword } = useAuthService();

  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canEditOrDeleteUsers = isAdministrator();

  /** 🔹 Cargar lista de usuarios */
  const loadUsers = useCallback((page: number = 1, search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    list(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? {};
          const items = Array.isArray(data.users) ? data.users : data.items ?? [];
          setUsers(items);
          
          // Actualizar información de paginación
          if (data.pagination) {
            setPagination({
              page: data.pagination.page || page,
              totalPages: data.pagination.total_pages || 1,
              totalCount: data.pagination.total_count || 0,
              pageSize: data.pagination.page_size || 10,
              hasNext: data.pagination.has_next || false,
              hasPrevious: data.pagination.has_previous || false,
            });
          }
        },
        onError: (resp: any) => {
          showToast(resp?.message ?? 'Error cargando usuarios', 'error');
        },
      },
      ctrl.signal
    );
  }, [list]);

  useEffect(() => {
    loadUsers();
    const meUser = getSessionUser<{ role?: string }>();
    setIsCurrentUserAdmin(meUser?.role === 'ADMIN');
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [loadUsers]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => loadUsers(1, value), 400);
    },
    [loadUsers]
  );

  /** 🔹 Crear usuario */
  const openCreate = () => {
    if (!canEditOrDeleteUsers) return;
    setEditing({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      is_active: true,
      role: 'GESTOR',
    });
    setFieldError(null);
    setShowForm(true);
  };

  /** 🔹 Editar usuario */
  const openEdit = (user: User) => {
    svc.edit(user.id!, {
      onSuccess: (resp: any) => {
        const data = resp.data ?? {};
        setEditing(data.user ?? user);
        setShowForm(true);
      },
      onError: () => showToast('Error cargando datos del usuario', 'error'),
    });
  };

  /** 🔹 Guardar usuario */
  const callbackSave = async (): Promise<any> => {
    if (!editing) return;
    if (!editing.username?.trim()) {
      setFieldError('El nombre de usuario es requerido');
      return { status: 2, message: 'Validation', data: { username: ['Campo requerido'] } };
    }
    if (!editing.email?.trim()) {
      setFieldError('El correo electrónico es requerido');
      return { status: 2, message: 'Validation', data: { email: ['Campo requerido'] } };
    }
    if (!editing.id && (!editing.password || !String(editing.password).trim())) {
      setFieldError('La contraseña es requerida al crear usuario');
      return { status: 2, message: 'Validation', data: { password: ['Campo requerido'] } };
    }
    const pwdTrim = String(editing.password ?? '').trim();
    if (!editing.id && pwdTrim) {
      const pwdErr = getPasswordRulesErrorMessage(pwdTrim);
      if (pwdErr) {
        setFieldError(pwdErr);
        return { status: 2, message: 'Validation', data: { password: [pwdErr] } };
      }
    }
    if (editing.id && isCurrentUserAdmin && pwdTrim) {
      const pwdErr = getPasswordRulesErrorMessage(pwdTrim);
      if (pwdErr) {
        setFieldError(pwdErr);
        return { status: 2, message: 'Validation', data: { password: [pwdErr] } };
      }
    }

    setFieldError(null);
    setSavingLocked(true);

    try {
      if (editing.id) {
        const newPassword = String(editing.password ?? '').trim();
        const { password: _, ...payload } = editing;
        const updateResp = await update(editing.id, payload as User);

        if (isCurrentUserAdmin && newPassword) {
          await adminResetPassword(editing.id, newPassword);
        }

        return updateResp;
      }
      return await store(editing);
    } catch (error: any) {
      console.error('[users] Error en callbackSave:', error);
      let errMsg = error?.message as string | undefined;
      const errs = error?.errors;
      if (errs && typeof errs === 'object' && !Array.isArray(errs)) {
        const flat = Object.values(errs)
          .flat()
          .map((x) => (Array.isArray(x) ? x.join(' ') : String(x)))
          .filter(Boolean);
        if (flat.length) errMsg = flat.join(' ');
      }
      errMsg = errMsg || 'Error guardando usuario';
      setFieldError(errMsg);
      return { status: 2, message: errMsg, http_status: error?.http_status ?? 400 };
    } finally {
      setSavingLocked(false);
    }
  };

  /** 🔹 Post-guardado */
  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado correctamente', 'success');
      setShowForm(false);
      loadUsers(pagination.page, searchTextRef.current.trim() || undefined);
      return;
    }
    showToast(resp?.message ?? 'Error guardando usuario', 'error');
  };

  /** 🔹 Eliminar usuario */
  const openDeleteConfirmation = (id: number) => {
    setUserIdToDelete(id);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = () => {
    if (userIdToDelete === null) return;

    remove(userIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado correctamente', 'success');
        loadUsers(pagination.page, searchTextRef.current.trim() || undefined);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando usuario', 'error'),
    });

    setIsAlertOpen(false);
    setUserIdToDelete(null);
  };

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setUserIdToDelete(null);
  };

  return (
    <div className="users-page">
      <header className="users-header">
        <h1>Usuarios</h1>
        {canEditOrDeleteUsers && (
          <div>
            <button className="btn-create" onClick={openCreate}>
              + Nuevo Usuario
            </button>
          </div>
        )}
      </header>

      <section className="users-meta">
        <span>{fetching.list ? 'Cargando...' : `${users.length} usuarios`}</span>
      </section>

      <TableTextSearch
        value={searchText}
        onChange={handleSearchChange}
        placeholder="Buscar por usuario, correo, nombre o RUT..."
      />

      <UsersTable
        data={users}
        loading={fetching.list}
        onEdit={canEditOrDeleteUsers ? openEdit : undefined}
        onDelete={canEditOrDeleteUsers ? openDeleteConfirmation : undefined}
      />
      
      {pagination.totalPages > 1 && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          pageSize={pagination.pageSize}
          onPageChange={(page) => {
            loadUsers(page, searchTextRef.current.trim() || undefined);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}

      {showForm && editing && (
        <div className="users-modal">
          <div className="users-modal-inner">
            <h3>{editing.id ? 'Editar usuario' : 'Crear usuario'}</h3>

            <UserForm
              value={editing}
              onChange={setEditing}
              error={fieldError || undefined}
              onClose={() => setShowForm(false)}
              canResetPassword={isCurrentUserAdmin}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={() => setShowForm(false)} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar usuario?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />
    </div>
  );
};

export default UsersPage;
